import React, {FC, useState} from "react";
import {
  Container,
  CssBaseline,
  Avatar,
  Button,
  Typography,
  ThemeProvider,
  Box,
  createTheme,
  Snackbar,
  Alert,
  FilledInput,
  IconButton,
  InputAdornment,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import {VisibilityOff, Visibility} from "@mui/icons-material";
import {useSearchParams} from "react-router-dom";
import {
  useResetPasswordMutation,
  useValidateResetTokenQuery,
} from "../../app/services/account";

const theme = createTheme();

/**
 * Completes a password reset. Reads the email and token from the query string (as embedded in the
 * emailed link), collects a new password, and submits it to the API.
 */
const ResetPasswordPage: FC = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";
  const token = searchParams.get("token") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [resetPassword, {isLoading}] = useResetPasswordMutation();

  const isLinkValid = Boolean(email) && Boolean(token);

  // Validate the token on load (before showing an editable form) so an expired/invalid link is
  // caught up front. Skipped when the link is missing params — that case is handled on its own.
  // This is an early UX check only; the authoritative server check still runs on submit.
  const {
    data: tokenCheck,
    isFetching: isValidatingToken,
    isError: tokenCheckFailed,
  } = useValidateResetTokenQuery({email, token}, {skip: !isLinkValid});

  // Only treat the token as unusable when the server definitively says so. If the check itself
  // errors (network/500), fall back to showing the form so a transient blip can't flag a good
  // link as expired — the submit-time check remains the real gate.
  const isTokenExpired = isLinkValid && !tokenCheckFailed && tokenCheck?.isValid === false;

  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    await resetPassword({email, token, newPassword, confirmPassword})
      .unwrap()
      .then(() => {
        setIsSuccess(true);
      })
      .catch(error => {
       
        const errors = error?.data?.Errors as Record<string, string[]> | undefined;
        const firstErrorMessage = errors && Object.values(errors)[0]?.[0];

        setSnackbarMessage(
          firstErrorMessage ??
            "This password reset link is invalid or has expired. Please request a new one.",
        );
        setIsSnackbarOpen(true);
      });
  }

  /** An error state that explains why the reset link can't be used. */
  function renderLinkError(message: string) {
    return (
      <Box sx={{mt: 3, textAlign: "center"}}>
        <Alert severity="error">{message}</Alert>
      </Box>
    );
  }

  /**
   * Renders the body for the current state: success, invalid/incomplete link, verifying the link,
   * an expired/invalid token, or the reset form.
   */
  function renderBody() {
    if (isSuccess) {
      return (
        <Box sx={{mt: 3, textAlign: "center"}}>
          <Alert severity="success" sx={{mb: 2}}>
            Your password has been reset. Please return to your app and sign in with your
            new password.
          </Alert>
        </Box>
      );
    }

    if (!isLinkValid) {
      return renderLinkError(
        "This password reset link is invalid or incomplete. Please request a new one.",
      );
    }

    // Verifying the token against the server before showing an editable form.
    if (isValidatingToken) {
      return (
        <Box sx={{mt: 3, display: "flex", justifyContent: "center"}}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    // Token already known to be invalid/expired: don't present a usable form.
    if (isTokenExpired) {
      return renderLinkError(
        "This password reset link is invalid or has expired. Please request a new one.",
      );
    }

    return (
      <Box component="form" onSubmit={handleSubmit} sx={{mt: 1, width: "100%"}}>
        <FormControl sx={{my: 1, width: "100%"}} variant="filled">
          <InputLabel htmlFor="newPassword">New password</InputLabel>
          <FilledInput
            name="newPassword"
            type={isPasswordVisible ? "text" : "password"}
            required
            autoFocus
            autoComplete="new-password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  onMouseDown={e => e.preventDefault()}
                  edge="end">
                  {isPasswordVisible ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            sx={{input: {background: "white"}}}
          />
        </FormControl>

        <FormControl sx={{my: 1, width: "100%"}} variant="filled">
          <InputLabel htmlFor="confirmPassword">Confirm new password</InputLabel>
          <FilledInput
            name="confirmPassword"
            type={isPasswordVisible ? "text" : "password"}
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            error={confirmPassword.length > 0 && !passwordsMatch}
            sx={{input: {background: "white"}}}
          />
        </FormControl>

        {confirmPassword.length > 0 && !passwordsMatch && (
          <Typography variant="body2" color="error" sx={{my: 1}}>
            Passwords do not match.
          </Typography>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{mt: 3, mb: 2}}
          disabled={!passwordsMatch || isLoading}>
          Reset password
          {isLoading && (
            <CircularProgress
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: -12,
                marginLeft: -12,
              }}
              size={24}
            />
          )}
        </Button>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="sm">
        <CssBaseline />

        <Snackbar
          open={isSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => setIsSnackbarOpen(false)}
          anchorOrigin={{vertical: "top", horizontal: "center"}}>
          <Alert severity="error" onClose={() => setIsSnackbarOpen(false)}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Box
          sx={{
            my: 16,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}>
          <Avatar sx={{m: 1, bgcolor: "secondary.main"}}>
            <LockResetIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Reset password
          </Typography>

          {renderBody()}
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default ResetPasswordPage;
